import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL
const chaveAnon = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !chaveAnon) {
  throw new Error(
    'Faltam VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY. Copie .env.example para .env.local.',
  )
}

/**
 * Cliente Supabase da aplicação.
 *
 * Assim que o projeto existir, rode `pnpm db:types` e troque a chamada
 * por `createClient<Database>(...)` importando de '@/tipos/supabase'.
 * Até lá as telas se apoiam nas interfaces de '@/tipos/tabelas'.
 */
export const supabase = createClient(url, chaveAnon, {
  auth: {
    // OTP por e-mail: sem senha, sem link mágico. Menos pontos de perda
    // de acesso para um público que usa o app todo dia por 28 dias.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storageKey: 'viver.auth',
  },
  global: {
    headers: { 'x-app': 'projeto-viver' },
  },
})

/** Sessão atual, ou null. */
export async function sessaoAtual() {
  const { data } = await supabase.auth.getSession()
  return data.session
}

/** Envia o código de 6 dígitos para o e-mail. */
export async function enviarCodigo(email: string) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: { shouldCreateUser: true },
  })
  if (error) throw error
}

/**
 * Confirma o código recebido por e-mail.
 *
 * Uma tentativa só, com `type: 'email'` — o tipo documentado para o par
 * `signInWithOtp` + `verifyOtp`.
 *
 * Antes isto tentava `'signup'` e depois `'email'`, para cobrir os dois
 * fluxos do Supabase. Foi um erro: o token de OTP é de uso único, então
 * a primeira tentativa malsucedida já o consome e a segunda encontra um
 * código morto. A "proteção" garantia a falha que tentava evitar.
 *
 * Para isto funcionar, "Confirm email" precisa estar DESLIGADO no painel
 * (Authentication › Sign In / Providers › Email). Com ele ligado, a
 * participante nova entra num fluxo de confirmação de cadastro em vez do
 * fluxo de OTP — e nesse caso o código chega, mas não valida. Confirmar
 * e-mail aqui é redundante de qualquer forma: receber o código já prova
 * o acesso à caixa.
 */
export async function confirmarCodigo(email: string, codigo: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.trim().toLowerCase(),
    token: codigo.trim(),
    type: 'email',
  })

  if (error) {
    // Propaga a mensagem real do Supabase. Trocar por um texto genérico
    // deixou este bug muito mais difícil de diagnosticar do que precisava.
    console.error('[auth] verifyOtp falhou:', error.status, error.message)
    throw error
  }

  return data.session
}

export async function sair() {
  await supabase.auth.signOut()
}

/**
 * URL assinada para um arquivo em bucket privado.
 *
 * As fotos "antes" e "depois" nunca têm URL pública: o bucket é privado
 * e todo acesso passa por um link de vida curta.
 */
export async function urlAssinada(bucket: string, caminho: string, segundos = 60 * 10) {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(caminho, segundos)
  if (error) throw error
  return data.signedUrl
}
